import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { parseISO, startOfDay } from "date-fns";
import { X, Clock, Send, Users, Loader2, Calendar, CheckCircle2, Pencil } from "lucide-react";
import { useAppDispatch } from "../../../../redux/store";
import { RootState } from "../../../../redux/store";
import { DateTimeUtil } from "../../../../utils/dateTime";
import {
    getOpenShiftDetails,
    claimOpenShifts,
    cancelOpenShiftRequest,
    cancelOpenShift,
} from "../../../../redux/features/openShift";
import { OpenShiftDetail } from "../../../../types/model/openShift";
import { ClaimOpenShiftRequest } from "../../../../types/request";
import { toast } from "react-toastify";
import ClaimConfirmationModal from "../../../../components/OpenShifts/ClaimConfirmationModal";
import CancelConfirmationModal from "../../../../components/OpenShifts/CancelConfirmationModal";
import type { ScheduleV3OpenShift, ScheduleV3Group } from "../../../../types";

export interface OpenShiftDetailModalProps {
    open: boolean;
    onClose: () => void;
    /** Transaction id for getOpenShiftDetails and claim APIs */
    shiftId: string | null;
    /** Initial data from grid for display before API load */
    initialOpenShift?: ScheduleV3OpenShift | null;
    day?: Date;
    group?: ScheduleV3Group;
    hasScheduleToolPrivilege: boolean;
    /** Called when manager clicks Assign – parent should open AssignOpenShiftModal with same payload */
    onAssign?: (payload: { openShift: ScheduleV3OpenShift; day: Date; group: ScheduleV3Group }) => void;
    /** Called when manager clicks Publish – parent should open publish confirmation */
    onPublish?: () => void;
    /** Called after manager successfully cancels the open shift */
    onCancelShiftSuccess?: () => void;
}

function getStatusDisplay(status: string | undefined): "pending" | "approved" | "cancelled" {
    const s = (status || "").toLowerCase();
    if (s === "approved" || s === "1") return "approved";
    if (s === "cancelled" || s === "rejected") return "cancelled";
    return "pending";
}

const OpenShiftDetailModal: React.FC<OpenShiftDetailModalProps> = ({
    open,
    onClose,
    shiftId,
    initialOpenShift,
    day,
    group,
    hasScheduleToolPrivilege,
    onAssign,
    onPublish,
    onCancelShiftSuccess,
}) => {
    const dispatch = useAppDispatch();
    const { selectedOpenShiftDetail, isLoadingOpenShiftDetails, error } = useSelector(
        (state: RootState) => state.openShift
    );
    const [details, setDetails] = useState<OpenShiftDetail | null>(null);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [isClaiming, setIsClaiming] = useState(false);
    const [isCanceling, setIsCanceling] = useState(false);
    const [showCancelShiftModal, setShowCancelShiftModal] = useState(false);
    const [isCancellingShift, setIsCancellingShift] = useState(false);

    useEffect(() => {
        if (!open) return;
        // Do not call any APIs on modal open; rely on grid data.
        setDetails(null);
    }, [open, shiftId, dispatch]);

    useEffect(() => {
        if (selectedOpenShiftDetail) {
            setDetails(selectedOpenShiftDetail);
        }
    }, [selectedOpenShiftDetail]);

    const transactionId = shiftId || details?.os_sch_open_shift_transactions_id || "";

    const shiftDate = details?.shift_date || initialOpenShift?.shift_date || "";
    const shiftName = details?.shift_name || initialOpenShift?.shift_details?.shift_name || "Open Shift";
    const startTime = details?.shift_start_time || initialOpenShift?.shift_details?.shift_start_time || "";
    const endTime = details?.shift_end_time || initialOpenShift?.shift_details?.shift_end_time || "";
    const createdBy = details?.created_details?.role || initialOpenShift?.manager_details?.name || "Manager";
    const rawStatus = (details?.status || (initialOpenShift as any)?.status || "").toString().trim().toLowerCase();
    const isPublished =
        rawStatus === "published" ||
        rawStatus.includes("publish");
    const statusKind: "published" | "draft" | "other" =
        rawStatus === "published" ? "published" : rawStatus === "draft" ? "draft" : "other";

    const shiftStartDateTime = shiftDate && startTime ? `${shiftDate} ${startTime}` : undefined;
    const shiftEndDateTime = shiftDate && endTime ? `${shiftDate} ${endTime}` : undefined;
    const prettyTimeRange =
        shiftStartDateTime && shiftEndDateTime
            ? DateTimeUtil.formatDateTimeRange(shiftStartDateTime, shiftEndDateTime, "", false)
            : "";
    const prettyDate = shiftDate ? DateTimeUtil.formatWithDayjs(shiftDate, "MMM D, YYYY") : "";
    const isPastDate = (() => {
        if (!shiftDate) return false;
        try {
            return parseISO(shiftDate) < startOfDay(new Date());
        } catch {
            return false;
        }
    })();

    const managerName =
        (details as any)?.manager_details?.name ||
        (details as any)?.created_details?.name ||
        initialOpenShift?.manager_details?.name ||
        "";
    const managerImageUrl =
        (details as any)?.manager_details?.image_url ||
        (details as any)?.created_details?.image_url ||
        initialOpenShift?.manager_details?.image_url ||
        "";
    const notes =
        (details as any)?.notes ??
        (initialOpenShift as any)?.notes ??
        "";
    const departmentName =
        (details as any)?.department?.department_name ||
        (initialOpenShift as any)?.department?.department_name ||
        "";
    const departmentCode =
        (details as any)?.department?.department_code ||
        (initialOpenShift as any)?.department?.department_code ||
        "";
    const siteName =
        (details as any)?.site?.site_name ||
        (initialOpenShift as any)?.site?.site_name ||
        "";
    const siteCode =
        (details as any)?.site?.site_code ||
        (initialOpenShift as any)?.site?.site_code ||
        "";
    const workCodeName =
        (details as any)?.workcode?.work_code_name ||
        (initialOpenShift as any)?.workcode?.work_code_name ||
        "";
    const workCode =
        (details as any)?.workcode?.work_code ||
        (initialOpenShift as any)?.workcode?.work_code ||
        "";
    const shiftTags: Array<{
        field_code?: string;
        field_name?: string;
        field_values?: Array<{ value_code?: string; value_text?: string }>;
    }> =
        ((details as any)?.shift_tags as any[]) ||
        ((initialOpenShift as any)?.shift_tags as any[]) ||
        [];

    const handleClaimConfirm = async (notes: string) => {
        if (!transactionId) {
            toast.error("Missing shift id");
            return;
        }
        setIsClaiming(true);
        try {
            const claimData: ClaimOpenShiftRequest = {
                shift_ids: [transactionId],
                notes: notes.trim() || undefined,
            };
            const message = await dispatch(claimOpenShifts(claimData)).unwrap();
            toast.success(message || "Shift claimed successfully!");
            setShowClaimModal(false);
            // Refresh details only after successful action
            dispatch(getOpenShiftDetails({ shift_id: transactionId }));
        } catch (err: any) {
            toast.error(typeof err === "string" ? err : err?.message || "Failed to claim shift");
        } finally {
            setIsClaiming(false);
        }
    };

    const handleCancelRequest = async () => {
        if (!transactionId) return;
        setIsCanceling(true);
        try {
            await dispatch(cancelOpenShiftRequest(transactionId)).unwrap();
            toast.success("Request cancelled");
            // Refresh details only after successful action
            dispatch(getOpenShiftDetails({ shift_id: transactionId }));
        } catch (err: any) {
            toast.error(err?.message || "Failed to cancel request");
        } finally {
            setIsCanceling(false);
        }
    };

    const handleCancelShiftConfirm = async () => {
        if (!transactionId) return;
        setIsCancellingShift(true);
        try {
            await dispatch(cancelOpenShift(transactionId)).unwrap();
            toast.success("Open shift cancelled");
            setShowCancelShiftModal(false);
            onCancelShiftSuccess?.();
            onClose();
        } catch (err: any) {
            toast.error(typeof err === "string" ? err : err?.message || "Failed to cancel shift");
        } finally {
            setIsCancellingShift(false);
        }
    };

    const handleAssign = () => {
        if (initialOpenShift && day && group && onAssign) {
            onAssign({ openShift: initialOpenShift, day, group });
            onClose();
        }
    };

    if (!open) return null;

    return (
        <>
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1300] p-4">
                <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between p-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Open Shift Details</h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
                        >
                            <X size={20} />
                        </button>
                    </div>
                    <div className="p-4 overflow-y-auto flex-1 space-y-4">
                        {error && !details && (
                            <div className="text-sm text-red-600 py-2">
                                {error}
                            </div>
                        )}
                        {(details || initialOpenShift) && (
                            <>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="text-xl font-semibold text-gray-900 leading-snug">
                                            {shiftName}
                                        </span>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        {!!prettyTimeRange && (
                                            <div className="flex items-center text-sm text-gray-700 gap-2">
                                                <Clock size={16} className="text-gray-500" />
                                                <span className="font-medium">{prettyTimeRange}</span>
                                            </div>
                                        )}
                                        {!!prettyDate && (
                                            <div className="flex items-center text-sm text-gray-600 gap-2">
                                                <Calendar size={16} className="text-gray-500" />
                                                <span>{prettyDate}</span>
                                            </div>
                                        )}
                                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                                            <span>Posted by</span>
                                            {managerImageUrl ? (
                                                <img
                                                    src={managerImageUrl}
                                                    alt={managerName || createdBy}
                                                    className="w-5 h-5 rounded-full object-cover border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-5 h-5 rounded-full bg-gray-100 border border-gray-200 flex-shrink-0" />
                                            )}
                                            <span className="font-medium text-gray-700">{managerName || createdBy}</span>
                                        </div>
                                    </div>
                                </div>

                                {(rawStatus || notes || departmentName || departmentCode || siteName || siteCode || workCodeName || workCode || (shiftTags && shiftTags.length > 0)) && (
                                    <div className="rounded-lg border border-gray-200 bg-white p-3 space-y-3">
                                        <div className="text-sm font-semibold text-gray-800">Details</div>

                                        {rawStatus && (
                                            <div className="flex items-center justify-between text-sm gap-3">
                                                <span className="text-gray-600">Status</span>
                                                <span
                                                    className={
                                                        statusKind === "published"
                                                            ? "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border bg-green-50 text-green-700 border-green-200"
                                                            : statusKind === "draft"
                                                              ? "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border bg-amber-50 text-amber-700 border-amber-200"
                                                              : "inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-semibold border bg-gray-50 text-gray-700 border-gray-200"
                                                    }
                                                    title={rawStatus}
                                                >
                                                    {statusKind === "published" ? (
                                                        <CheckCircle2 size={14} />
                                                    ) : statusKind === "draft" ? (
                                                        <Pencil size={14} />
                                                    ) : null}
                                                    <span className="capitalize">
                                                        {statusKind === "other" ? rawStatus : statusKind}
                                                    </span>
                                                </span>
                                            </div>
                                        )}

                                        {(departmentName || departmentCode) && (
                                            <div className="flex items-center justify-between text-sm gap-3">
                                                <span className="text-gray-600">Department</span>
                                                <span className="font-medium text-gray-900 text-right">
                                                    {departmentName || departmentCode}
                                                </span>
                                            </div>
                                        )}

                                        {(siteName || siteCode) && (
                                            <div className="flex items-center justify-between text-sm gap-3">
                                                <span className="text-gray-600">Site</span>
                                                <span className="font-medium text-gray-900 text-right">
                                                    {siteName || siteCode}
                                                </span>
                                            </div>
                                        )}

                                        {(workCodeName || workCode) && (
                                            <div className="flex items-center justify-between text-sm gap-3">
                                                <span className="text-gray-600">Work code</span>
                                                <span className="font-medium text-gray-900 text-right">
                                                    {workCodeName || workCode}
                                                </span>
                                            </div>
                                        )}

                                        {notes && String(notes).trim() && (
                                            <div className="text-sm">
                                                <div className="text-gray-600 mb-1">Notes</div>
                                                <div className="text-gray-900 whitespace-pre-wrap break-words">
                                                    {String(notes)}
                                                </div>
                                            </div>
                                        )}

                                        {shiftTags && shiftTags.length > 0 && (
                                            <div className="text-sm">
                                                <div className="text-gray-600 mb-1">Tags</div>
                                                <div className="flex flex-wrap gap-2">
                                                    {shiftTags
                                                        .flatMap((t) => {
                                                            const values = Array.isArray(t.field_values) ? t.field_values : [];
                                                            const labelBase = t.field_name || t.field_code || "Tag";
                                                            return values
                                                                .map((v) => ({
                                                                    key: `${t.field_code || labelBase}:${v.value_code || v.value_text || ""}`,
                                                                    text: `${labelBase}: ${v.value_text || v.value_code || ""}`.trim(),
                                                                }))
                                                                .filter((x) => x.text && !x.text.endsWith(":"));
                                                        })
                                                        .map((t) => (
                                                            <span
                                                                key={t.key}
                                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                                                            >
                                                                {t.text}
                                                            </span>
                                                        ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                            </>
                        )}

                        {/* Loader at bottom so it doesn't cover existing data */}
                        {isLoadingOpenShiftDetails && (
                            <div className="flex items-center justify-center py-4">
                                <Loader2 size={20} className="animate-spin text-purple-600" />
                            </div>
                        )}
                    </div>
                    <div className="p-4 border-t border-gray-200 flex flex-wrap gap-2 justify-between">
                        <div>
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                        <button
                            type="button"
                            onClick={onClose}
                            className="h-10 px-4 border border-gray-300 rounded-lg text-gray-700 text-sm hover:bg-gray-50"
                        >
                            Close
                        </button>
                        {!hasScheduleToolPrivilege && transactionId && !isPastDate && (
                            <button
                                type="button"
                                onClick={() => setShowClaimModal(true)}
                                className="h-10 px-4 border border-blue-600 text-blue-600 text-sm rounded-lg bg-transparent hover:bg-blue-50"
                            >
                                Claim shift
                            </button>
                        )}
                        {hasScheduleToolPrivilege && (
                            <>
                                {!isPastDate && (statusKind === "draft" || statusKind === "published") && (
                                    <button
                                        type="button"
                                        onClick={() => setShowCancelShiftModal(true)}
                                        className="h-10 px-4 border border-red-600 text-red-600 text-sm rounded-lg bg-transparent hover:bg-red-50"
                                    >
                                        Cancel Shift
                                    </button>
                                )}
                                {/* Only show Publish based on API response (avoid flashing during load) */}
                                {onPublish && !isLoadingOpenShiftDetails && !!details && !isPublished && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onPublish();
                                            onClose();
                                        }}
                                        className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 flex items-center gap-2"
                                    >
                                        <Send size={14} />
                                        Publish
                                    </button>
                                )}
                                {!isPastDate && onAssign && initialOpenShift && day && group && statusKind === "published" && (
                                    <button
                                        type="button"
                                        onClick={handleAssign}
                                        className="h-10 px-4 border border-purple-600 text-purple-700 text-sm rounded-lg bg-transparent hover:bg-purple-50 flex items-center gap-2"
                                    >
                                        <Users size={14} />
                                        Assign
                                    </button>
                                )}
                            </>
                        )}
                        </div>
                    </div>
                </div>
            </div>
            <ClaimConfirmationModal
                isOpen={showClaimModal}
                onClose={() => setShowClaimModal(false)}
                onConfirm={handleClaimConfirm}
                selectedCount={1}
                isLoading={isClaiming}
            />
            <CancelConfirmationModal
                isOpen={showCancelShiftModal}
                onClose={() => setShowCancelShiftModal(false)}
                onConfirm={handleCancelShiftConfirm}
                shiftLocation={shiftName}
                shiftDate={prettyDate}
                isLoading={isCancellingShift}
                title="Cancel Open Shift"
                message="Are you sure you want to cancel this open shift?"
                confirmLabel="Yes, Cancel Shift"
            />
        </>
    );
};

export default OpenShiftDetailModal;
